import { useEffect, useRef } from 'react';

interface MessageContentParserProps {
  content: string;
  className?: string;
}

function MessageContentParser({ content, className }: MessageContentParserProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Function to convert plain URLs to clickable links
  const linkifyUrls = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      // Regex to match URLs
      const urlRegex = /(?:https?:\/\/|www\.)[^\s<]+(?:\.[^\s<]+)+(?:\/[^\s<]*)?/gi;

      if (urlRegex.test(text)) {
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;
        let match;

        urlRegex.lastIndex = 0; // Reset regex
        while ((match = urlRegex.exec(text)) !== null) {
          // Add text before the URL
          if (match.index > lastIndex) {
            fragment.appendChild(document.createTextNode(text.substring(lastIndex, match.index)));
          }

          // Create link element
          const link = document.createElement('a');
          let href = match[0];
          // Add protocol if missing
          if (!href.startsWith('http://') && !href.startsWith('https://')) {
            href = `https://${href}`;
          }
          link.href = href;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.textContent = match[0];
          link.className = 'text-blue-500 underline hover:text-blue-700';
          fragment.appendChild(link);

          lastIndex = match.index + match[0].length;
        }

        // Add remaining text
        if (lastIndex < text.length) {
          fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
        }

        // Replace the text node with the fragment
        if (node.parentNode) {
          node.parentNode.replaceChild(fragment, node);
        }
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Skip certain elements
      const element = node as HTMLElement;
      if (element.tagName !== 'A' && element.tagName !== 'CODE' && element.tagName !== 'PRE') {
        // Process child nodes - iterate backwards to avoid issues with DOM mutations
        const childNodes = Array.from(node.childNodes);
        // Process nodes in reverse order to handle DOM mutations safely
        for (let i = childNodes.length - 1; i >= 0; i--) {
          linkifyUrls(childNodes[i]);
        }
      }
    }
  };

  useEffect(() => {
    if (!containerRef.current) return;

    // First, convert markdown links to HTML
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const contentWithHtmlLinks = content.replace(markdownLinkRegex, (match, text, url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-500 underline hover:text-blue-700">${text}</a>`;
    });

    // Set the HTML content with converted links
    containerRef.current.innerHTML = contentWithHtmlLinks;

    // Convert remaining plain URLs to links
    linkifyUrls(containerRef.current);
  }, [content]);

  return <div ref={containerRef} className={className} dir="auto" />;
}

export default MessageContentParser;
